import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LogOut,
  Phone,
  Sparkles,
  CalendarCheck,
  ChevronRight,
  CheckCircle,
  Clock,
  Compass,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, b] = await Promise.all([api.get('/services'), api.get('/bookings')]);
        setServices(s.data);
        setBookings(b.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onLogout = async () => {
    await logout();
    navigate('/');
  };

  const discovery = services.find((s) => s.service_id === 'discovery');
  const generic = services.find((s) => s.service_id === 'generic');
  const paidServices = services.filter((s) => !s.is_free && s.service_id !== 'generic');
  const curatedIds = user?.curated_services || [];
  const curatedServices = curatedIds
    .map((id) => services.find((s) => s.service_id === id))
    .filter(Boolean);
  const firstName = (user?.name || 'Friend').split(' ')[0];

  return (
    <div className="dashboard" data-testid="dashboard-page">
      <header className="dashboard-header">
        <div className="dashboard-header-inner">
          <Link to="/" className="dashboard-logo">SOUL ALIGNED</Link>
          <div className="dashboard-user">
            <span className="dashboard-user-name" data-testid="dashboard-user-name">
              {user?.name}
              {user?.role === 'admin' && <span className="badge-admin">Admin</span>}
            </span>
            {user?.role === 'admin' && (
              <Link to="/admin" className="btn-ghost" data-testid="admin-link">
                Admin Panel
              </Link>
            )}
            <button onClick={onLogout} className="btn-ghost" data-testid="logout-btn">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        <section className="dashboard-hero">
          <h1>Hello, {firstName} <span className="wave">👋</span></h1>
          <p>Where would you like to begin your journey today?</p>
        </section>

        {/* Discovery Call CTA */}
        {discovery && (
          <section className="dash-discovery" data-testid="dash-discovery-card">
            <div className="dash-discovery-icon"><Phone size={28} /></div>
            <div className="dash-discovery-content">
              <h2>Not sure which service is right for you?</h2>
              <p>
                Book a <strong>Free 15-min Discovery Call</strong> — share where you are, get a
                personalized recommendation, no payment needed.
              </p>
            </div>
            <button
              onClick={() => navigate(`/book/${discovery.service_id}`)}
              className="btn-primary btn-large"
              data-testid="book-discovery-btn"
            >
              Book Free Call <ChevronRight size={18} />
            </button>
          </section>
        )}

        {/* Generic Intake CTA */}
        {generic && (
          <section className="dash-generic" data-testid="dash-generic-card">
            <div className="dash-generic-icon"><Compass size={26} /></div>
            <div className="dash-generic-content">
              <h2>Want a full session but unsure which modality?</h2>
              <p>
                Take the <strong>Generic Intake Session</strong> — share your problem & expected outcome,
                and the healer picks NLP, Reiki or counselling based on what fits you best.
              </p>
            </div>
            <button
              onClick={() => navigate(`/book/${generic.service_id}`)}
              className="btn-secondary btn-large"
              data-testid="book-generic-btn"
            >
              Take Generic Intake <ChevronRight size={18} />
            </button>
          </section>
        )}

        {/* Curated recommendations (if admin has curated) */}
        {curatedServices.length > 0 && (
          <section className="dash-section" data-testid="curated-section">
            <div className="dash-section-head">
              <h2>Recommended just for you ✨</h2>
              <p>Hand-picked sessions based on your conversation with Hemant.</p>
            </div>
            <div className="dash-services-grid">
              {curatedServices.map((s) => (
                <button
                  key={s.service_id}
                  onClick={() => navigate(`/book/${s.service_id}`)}
                  className="dash-service-card dash-service-curated"
                  data-testid={`curated-service-${s.service_id}`}
                >
                  <span className="service-badge service-badge-recommended">
                    <Sparkles size={12} /> For you
                  </span>
                  <h3>{s.title}</h3>
                  <p className="dash-service-desc">{s.description}</p>
                  <div className="dash-service-foot">
                    <span className="dash-service-duration">{s.duration}</span>
                    <span className="dash-service-price">
                      {s.is_free ? 'Free' : `₹${s.price.toLocaleString('en-IN')}`}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Paid services grid */}
        <section className="dash-section">
          <div className="dash-section-head">
            <h2>I already know what I need</h2>
            <p>Select a session, fill a quick questionnaire, then proceed to payment.</p>
          </div>

          {loading ? (
            <div className="dash-loading">Loading services…</div>
          ) : (
            <div className="dash-services-grid" data-testid="dash-services-grid">
              {paidServices.map((s) => (
                <button
                  key={s.service_id}
                  onClick={() => navigate(`/book/${s.service_id}`)}
                  className="dash-service-card"
                  data-testid={`dash-service-${s.service_id}`}
                >
                  {s.badge && (
                    <span className={`service-badge service-badge-${s.badge.toLowerCase().replace(' ', '-')}`}>
                      <Sparkles size={12} /> {s.badge}
                    </span>
                  )}
                  <h3>{s.title}</h3>
                  <p className="dash-service-desc">{s.description}</p>
                  <div className="dash-service-foot">
                    <span className="dash-service-duration">{s.duration}</span>
                    <span className="dash-service-price">₹{s.price.toLocaleString('en-IN')}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* My bookings */}
        <section className="dash-section">
          <div className="dash-section-head">
            <h2>Your bookings</h2>
            <p>Sessions you&apos;ve requested.</p>
          </div>
          {bookings.length === 0 ? (
            <div className="dash-empty" data-testid="bookings-empty">
              <CalendarCheck size={32} />
              <p>No bookings yet. Start with the free discovery call above.</p>
            </div>
          ) : (
            <ul className="dash-bookings" data-testid="dash-bookings-list">
              {bookings.map((b) => (
                <li key={b.booking_id} className="dash-booking" data-testid={`booking-${b.booking_id}`}>
                  <div className="dash-booking-main">
                    <h4>{b.service_title}</h4>
                    <div className="dash-booking-meta">
                      <span className="meta-pill">
                        {b.status === 'confirmed' ? (
                          <><CheckCircle size={12} /> Confirmed</>
                        ) : (
                          <><Clock size={12} /> {b.status}</>
                        )}
                      </span>
                      <span className="meta-pill meta-pill-muted">
                        {b.is_free ? 'Free' : `₹${b.amount.toLocaleString('en-IN')} · ${b.payment_status}`}
                      </span>
                    </div>
                  </div>
                  <time className="dash-booking-time">
                    {new Date(b.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                  </time>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
